import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const ProficiencyRadar = () => {
  const [animationProgress, setAnimationProgress] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(1);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const skillData = [
    {
      skill: 'Frontend',
      current: 95,
      target: 98,
      growth: '+3%',
      icon: 'Monitor',
      color: '#00d4ff',
      projects: 50,
      experience: '5+ years'
    },
    {
      skill: 'Backend',
      current: 88,
      target: 92,
      growth: '+4%',
      icon: 'Server',
      color: '#00ff88',
      projects: 30,
      experience: '4+ years'
    },
    {
      skill: 'DevOps',
      current: 82,
      target: 88,
      growth: '+6%',
      icon: 'Settings',
      color: '#ff6b35',
      projects: 20,
      experience: '3+ years'
    },
    {
      skill: 'Design',
      current: 85,
      target: 90,
      growth: '+5%',
      icon: 'Paintbrush',
      color: '#8844ff',
      projects: 40,
      experience: '3+ years'
    },
    {
      skill: 'Mobile',
      current: 75,
      target: 82,
      growth: '+7%',
      icon: 'Smartphone',
      color: '#ffaa00',
      projects: 15,
      experience: '2+ years'
    },
    {
      skill: 'AI/ML',
      current: 70,
      target: 80,
      growth: '+10%',
      icon: 'Brain',
      color: '#ff4444',
      projects: 8,
      experience: '1+ years'
    }
  ];

  const radarData = skillData.map(skill => ({
    skill: skill.skill,
    current: skill.current * animationProgress,
    target: skill.target,
    fullMark: 100
  }));

  return (
    <div className="glass-morphism p-8 rounded-2xl cyber-border">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-orbitron font-bold text-2xl text-foreground mb-2">
            Proficiency Matrix
          </h3>
          <p className="text-text-secondary">
            Current capabilities vs future targets
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-brand-cyan rounded-full animate-pulse"></div>
          <span className="text-xs font-mono text-brand-cyan">SCANNING</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Radar Chart */}
        <div className="relative">
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid 
                  stroke="rgba(0, 212, 255, 0.3)" 
                  strokeWidth={1}
                />
                <PolarAngleAxis 
                  dataKey="skill" 
                  tick={{ 
                    fill: '#ffffff', 
                    fontSize: 12, 
                    fontFamily: 'Inter' 
                  }}
                />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, 100]} 
                  tick={{ 
                    fill: '#cccccc', 
                    fontSize: 10 
                  }}
                />
                <Radar
                  name="Current"
                  dataKey="current"
                  stroke="#00d4ff"
                  fill="rgba(0, 212, 255, 0.2)"
                  strokeWidth={2}
                />
                <Radar
                  name="Target"
                  dataKey="target"
                  stroke="#00ff88"
                  fill="transparent"
                  strokeWidth={1}
                  strokeDasharray="5 5"
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center space-x-6 mt-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-brand-cyan rounded-full"></div>
              <span className="text-xs text-text-secondary">Current Level</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-1 bg-brand-green"></div>
              <span className="text-xs text-text-secondary">Target Level</span>
            </div>
          </div>
        </div>

        {/* Skill Breakdown */}
        <div className="space-y-4">
          {skillData.map((skill, index) => (
            <motion.div
              key={skill.skill}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-lg cursor-pointer transition-all duration-300 ${
                selectedMetric === skill.skill
                  ? 'bg-surface cyber-border-active cyber-glow' :'bg-surface/50 cyber-border hover:bg-surface/70'
              }`}
              onClick={() => setSelectedMetric(selectedMetric === skill.skill ? null : skill.skill)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${skill.color}80, ${skill.color}40)` }}
                  >
                    <Icon name={skill.icon} size={16} color="#ffffff" />
                  </div>
                  <div>
                    <h4 className="font-exo font-semibold text-foreground">{skill.skill}</h4>
                    <p className="text-xs text-text-secondary">{skill.experience}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-mono font-bold text-brand-cyan">
                    {skill.current}%
                  </div>
                  <div className="text-xs text-brand-green">
                    {skill.growth}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="relative">
                <div className="w-full bg-background rounded-full h-2 mb-2">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ 
                      background: `linear-gradient(90deg, ${skill.color}, ${skill.color}80)`,
                      width: `${skill.current * animationProgress}%`
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${skill.current}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                  />
                </div>
                
                {/* Target Indicator */}
                <div 
                  className="absolute top-0 w-1 h-2 bg-brand-green"
                  style={{ left: `${skill.target}%` }}
                />
              </div>

              {/* Expanded Details */}
              {selectedMetric === skill.skill && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-border"
                >
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-text-tertiary">Projects:</span>
                      <span className="ml-2 text-brand-cyan font-mono">{skill.projects}</span>
                    </div>
                    <div>
                      <span className="text-text-tertiary">Target:</span>
                      <span className="ml-2 text-brand-green font-mono">{skill.target}%</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-background/50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="TrendingUp" size={14} color="#00ff88" />
                      <span className="text-xs font-semibold text-brand-green">Growth Plan</span>
                    </div>
                    <p className="text-xs text-text-secondary">
                      Targeting {skill.growth} improvement through advanced projects and continuous learning.
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProficiencyRadar;